import type { ReactNode, FC } from 'react';

import { useEffect, useRef } from 'react';

import useClassy from '@core/hooks/useClassy';
import usePlanPageUrl from '@core/hooks/usePlanPageUrl';
import { useSuperHubStore } from '@core/store';
import upgradePlanData from '@routes/Dash/ManagePlans/PlanSelector/data';
import Button from '@ui/Button';
import Flex from '@ui/Flex';
import Icon from '@ui/Icon';
import Modal, { ModalBody, ModalFooter } from '@ui/Modal';

import useNavigationHandlers from '../../hooks/useNavigationHandlers';

import classes from './index.module.scss';

interface Feature {
  icon: string;
  label: string;
}

interface PlanCardProps {
  actionHref?: string;
  className?: string;
  description: string;
  features: readonly Feature[];
  featuresIntro: string;
  isHighlighted?: boolean;
  price: ReactNode;
  title: string;
}

const PlanCard: FC<PlanCardProps> = ({
  actionHref,
  description,
  title,
  price,
  features,
  isHighlighted = false,
  featuresIntro,
}) => {
  const bem = useClassy(classes, 'TrialExpiredModal-planCard');

  return (
    <div className={bem('&', isHighlighted && '_highlighted')}>
      <div>
        <Flex align="baseline" gap="size-8" justify="between">
          <div className={bem('-title')}>{title}</div>
          <Flex align="baseline" className={bem('-price')} gap="size-2" justify="start">
            <div className={bem('-price-amount')}>${price}</div>
            <div className={bem('-price-frequency')}>/month</div>
          </Flex>
        </Flex>
        <div className={bem('-description')}>{description}</div>
        <div className={bem('-featuresIntro')}>{featuresIntro}</div>
        <Flex align="start" className={bem('-featuresList')} gap="size-4" justify="start" layout="col" tag="ul">
          {features.map((feature, idx) => (
            <Flex
              key={idx}
              align="center"
              className={bem('-featuresList-feature')}
              gap="size-8"
              justify="start"
              tag="li"
            >
              <Flex align="center" className={bem('-featuresList-feature-iconContainer')} gap={0} justify="center">
                <i
                  aria-hidden="true"
                  className={bem('-featuresList-feature-iconContainer-icon', 'fa-solid', 'fa-duotone', feature.icon)}
                />
              </Flex>
              <span className={bem('-featuresList-feature-label')}>{feature.label}</span>
            </Flex>
          ))}
        </Flex>
      </div>
      {!!actionHref && (
        <Flex className={bem('-usePlanButton')}>
          <Button fullWidth href={actionHref} kind={isHighlighted ? 'primary' : 'secondary'} outline={!isHighlighted}>
            Select Plan
          </Button>
        </Flex>
      )}
    </div>
  );
};

interface TrialExpiredModalProps {
  handleClose?: () => void;
  modalTargetId: string;
}

const TrialExpiredModal = ({ handleClose, modalTargetId }: TrialExpiredModalProps) => {
  const bem = useClassy(classes, 'TrialExpiredModal');
  const modalRef = useRef<Modal>(null);
  const isOwner = useSuperHubStore(s => s.user.isOwner);
  const { goToPreview } = useNavigationHandlers();

  const planPageUrl = usePlanPageUrl();

  const freelaunchCheckoutUrl = planPageUrl.isInternal ? `${planPageUrl.url}?plan=freelaunch` : undefined;
  const proCheckoutUrl = planPageUrl.isInternal ? `${planPageUrl.url}?plan=pro` : undefined;

  // Open modal when trial expires
  useEffect(() => {
    if (modalRef.current) {
      modalRef.current.toggle(true);
    }
  }, []);

  return (
    <>
      <Modal ref={modalRef} className={bem('&')} noDismiss size="lg" target={`#${modalTargetId}`} verticalCenter>
        <ModalBody>
          <Flex align="center" className={bem('-header')} gap={0} justify="center" layout="col">
            <div className={bem('-header-title')}>Your Pro plan free trial has ended</div>
            <p className={bem('-header-description')}>
              {isOwner
                ? 'Select a plan to continue editing and retain all features.'
                : 'Ask your project admin to select a plan to continue editing.'}
            </p>
          </Flex>
          <Flex align="stretch" gap="size-12" justify="start">
            <PlanCard
              actionHref={freelaunchCheckoutUrl}
              description={upgradePlanData.freelaunch.description}
              features={upgradePlanData.freelaunch.features}
              featuresIntro="Features include:"
              price={upgradePlanData.freelaunch.priceAnnual}
              title={upgradePlanData.freelaunch.title}
            />
            <PlanCard
              actionHref={proCheckoutUrl}
              description={upgradePlanData.pro.description}
              features={upgradePlanData.pro.features}
              featuresIntro="All Starter features, plus:"
              isHighlighted={true}
              price={upgradePlanData.pro.priceAnnual}
              title={upgradePlanData.pro.title}
            />
          </Flex>
          <div className={bem('-aiFeaturesContainer')}>
            <Flex className={bem('-aiFeaturesContainer-inner')} gap="size-8" justify="start">
              <Icon color="yellow" name="sparkles" size="lg" />
              <div>
                All paid plans include ReadMe&apos;s free AI features plus Agent Owlbert, AI Doc Linting, Docs Audit
                previews, and more.
              </div>
            </Flex>
          </div>
        </ModalBody>
        <ModalFooter align="center" className={bem('-footer')} justify="between">
          {handleClose ? (
            <Button ghost kind="secondary" onClick={handleClose} size="sm">
              Dismiss
            </Button>
          ) : (
            <Button ghost kind="secondary" onClick={goToPreview} size="sm">
              <Icon name="arrow-left" />
              Back to View Mode
            </Button>
          )}
          <Button href={planPageUrl.url} kind="secondary" outline size="sm" target={planPageUrl.target}>
            View All Plans
          </Button>
        </ModalFooter>
      </Modal>
      <div className={bem('-wrapper', 'ModalWrapper')} id={modalTargetId} />
    </>
  );
};

export default TrialExpiredModal;
